import {
 useId, useEffect, useMemo, useCallback 
} from 'react';
import { map } from './core/MapView';

/**
 * Esquemas de cores para o heatmap
 */
const COLOR_SCHEMES = {
  // Clássico: azul -> ciano -> verde -> amarelo -> vermelho
  classic: [
    [0, 'rgba(0, 0, 255, 0)'],
    [0.1, 'rgba(0, 0, 255, 0.4)'],
    [0.2, 'rgba(0, 255, 255, 0.6)'],
    [0.4, 'rgba(0, 255, 0, 0.7)'],
    [0.6, 'rgba(255, 255, 0, 0.8)'],
    [0.8, 'rgba(255, 128, 0, 0.9)'],
    [1, 'rgba(255, 0, 0, 1)'],
  ],
  // Fogo: preto -> vermelho -> laranja -> amarelo -> branco
  fire: [
    [0, 'rgba(0, 0, 0, 0)'],
    [0.2, 'rgba(128, 0, 0, 0.6)'],
    [0.4, 'rgba(255, 0, 0, 0.7)'],
    [0.6, 'rgba(255, 128, 0, 0.8)'],
    [0.8, 'rgba(255, 255, 0, 0.9)'],
    [1, 'rgba(255, 255, 255, 1)'],
  ],
  // Frio: azul escuro -> azul -> ciano -> branco
  cool: [
    [0, 'rgba(0, 0, 64, 0)'],
    [0.2, 'rgba(0, 0, 128, 0.5)'],
    [0.4, 'rgba(0, 64, 255, 0.6)'],
    [0.6, 'rgba(0, 128, 255, 0.7)'],
    [0.8, 'rgba(0, 255, 255, 0.85)'],
    [1, 'rgba(255, 255, 255, 1)'],
  ],
  // Viridis: roxo -> azul -> verde -> amarelo
  viridis: [
    [0, 'rgba(68, 1, 84, 0)'],
    [0.2, 'rgba(72, 40, 120, 0.5)'],
    [0.4, 'rgba(62, 74, 137, 0.6)'],
    [0.6, 'rgba(38, 130, 142, 0.7)'],
    [0.8, 'rgba(94, 201, 98, 0.85)'],
    [1, 'rgba(253, 231, 37, 1)'],
  ],
  // Densidade: transparente -> azul -> roxo
  density: [
    [0, 'rgba(0, 0, 255, 0)'],
    [0.3, 'rgba(65, 105, 225, 0.5)'],
    [0.5, 'rgba(138, 43, 226, 0.6)'],
    [0.7, 'rgba(199, 21, 133, 0.75)'],
    [0.9, 'rgba(255, 20, 147, 0.9)'],
    [1, 'rgba(255, 0, 128, 1)'],
  ],
};

/**
 * Converte array de cores para expressão MapLibre
 */
const buildColorExpression = (scheme) => {
  const colors = COLOR_SCHEMES[scheme] || COLOR_SCHEMES.classic;
  const expression = ['interpolate', ['linear'], ['heatmap-density']];
  colors.forEach(([stop, color]) => {
    expression.push(stop, color);
  });
  return expression;
};

/**
 * MapHeatLayer - Camada de mapa de calor para MapLibre GL
 *
 * Implementação completa baseada nos mapas PHP do GPSWOX:
 * - content_mapa_percurso_calor.php
 * - content_mapa_correlacao_calor.php
 * - content_mapa_evento_calor.php
 *
 * @param {Object} props
 * @param {Array} props.positions - Array de posições com latitude/longitude
 * @param {number} [props.radius=25] - Raio de cada ponto (pixels)
 * @param {number} [props.blur=15] - Blur do heatmap
 * @param {number} [props.intensity=1] - Intensidade do heatmap (0.1-3)
 * @param {number} [props.opacity=0.8] - Opacidade máxima
 * @param {number} [props.maxZoom=16] - Zoom máximo para renderização do heatmap
 * @param {string} [props.colorScheme='classic'] - Esquema de cores (classic, fire, cool, viridis, density)
 * @param {string} [props.weightProperty] - Propriedade para peso (ex: 'speed', 'altitude')
 * @param {boolean} [props.showPoints=true] - Mostrar pontos em zoom alto
 * @param {string} [props.pointColor='#ff6b6b'] - Cor dos pontos em zoom alto
 * @param {Function} [props.onClick] - Callback quando clicar em um ponto
 */
const MapHeatLayer = ({
  positions,
  radius = 25,
  blur = 15,
  intensity = 1,
  opacity = 0.8,
  maxZoom = 16,
  colorScheme = 'classic',
  weightProperty = null,
  showPoints = true,
  pointColor = '#ff6b6b',
  onClick = null,
}) => {
  const id = useId();
  const sourceId = `heat-source-${id}`;
  const heatLayerId = `heat-layer-${id}`;
  const circleLayerId = `heat-circle-${id}`;

  // Converte posições para GeoJSON com cálculo de peso
  const geojsonData = useMemo(() => {
    if (!positions?.length) {
      return { type: 'FeatureCollection', features: [] };
    }

    // Calcula min/max do weightProperty se especificado
    let minWeight = 0;
    let maxWeight = 1;

    if (weightProperty) {
      const weights = positions
        .map((p) => {
          const val = p[weightProperty] ?? p.attributes?.[weightProperty];
          return typeof val === 'number' ? val : null;
        })
        .filter((w) => w !== null);

      if (weights.length > 1) {
        minWeight = Math.min(...weights);
        maxWeight = Math.max(...weights);
      }
    }

    const weightRange = maxWeight - minWeight || 1;

    const features = positions.map((position, index) => {
      let weight = 1;

      if (weightProperty) {
        const rawWeight = position[weightProperty] ?? position.attributes?.[weightProperty];
        if (typeof rawWeight === 'number') {
          // Normaliza entre 0.1 e 1 para evitar pontos invisíveis
          weight = 0.1 + ((rawWeight - minWeight) / weightRange) * 0.9;
        }
      }

      return {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [position.longitude, position.latitude],
        },
        properties: {
          weight,
          id: position.id || index,
          deviceId: position.deviceId,
          speed: position.speed,
          altitude: position.altitude,
          fixTime: position.fixTime,
          address: position.address,
          index,
        },
      };
    });

    return { type: 'FeatureCollection', features };
  }, [positions, weightProperty]);

  // Handler de clique
  const handleClick = useCallback((e) => {
    if (onClick && e.features?.length) {
      const feature = e.features[0];
      const position = positions.find((p) => p.id === feature.properties.id);
      if (position) {
        onClick(position, feature);
      }
    }
  }, [onClick, positions]);

  // Inicializa source e layers
  useEffect(() => {
    // Adiciona source
    if (!map.getSource(sourceId)) {
      map.addSource(sourceId, {
        type: 'geojson',
        data: geojsonData,
      });
    }

    // Adiciona layer de heatmap
    if (!map.getLayer(heatLayerId)) {
      map.addLayer({
        id: heatLayerId,
        type: 'heatmap',
        source: sourceId,
        maxzoom: maxZoom + 1,
        paint: {
          // Peso baseado na propriedade weight
          'heatmap-weight': ['get', 'weight'],
          // Intensidade aumenta com zoom
          'heatmap-intensity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            0, intensity * 0.3,
            maxZoom / 2, intensity,
            maxZoom, intensity * 2,
          ],
          // Gradiente de cores
          'heatmap-color': buildColorExpression(colorScheme),
          // Raio aumenta com zoom
          'heatmap-radius': [
            'interpolate',
            ['exponential', 1.5],
            ['zoom'],
            0, radius * 0.3,
            maxZoom / 2, radius,
            maxZoom, radius * 2.5,
          ],
          // Opacidade diminui em zoom alto para transição suave
          'heatmap-opacity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            maxZoom - 2, opacity,
            maxZoom, 0,
          ],
        },
      });
    }

    // Adiciona layer de círculos para zoom alto (se habilitado)
    if (showPoints && !map.getLayer(circleLayerId)) {
      map.addLayer({
        id: circleLayerId,
        type: 'circle',
        source: sourceId,
        minzoom: maxZoom - 2,
        paint: {
          // Raio aumenta com zoom
          'circle-radius': [
            'interpolate',
            ['linear'],
            ['zoom'],
            maxZoom - 2, 2,
            maxZoom, 5,
            maxZoom + 2, 8,
          ],
          // Cor baseada no peso
          'circle-color': [
            'interpolate',
            ['linear'],
            ['get', 'weight'],
            0.1, '#3388ff',
            0.5, pointColor,
            1, '#ff0000',
          ],
          'circle-stroke-color': '#ffffff',
          'circle-stroke-width': [
            'interpolate',
            ['linear'],
            ['zoom'],
            maxZoom - 2, 0,
            maxZoom, 1,
          ],
          // Opacidade aumenta gradualmente
          'circle-opacity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            maxZoom - 2, 0,
            maxZoom - 1, 0.5,
            maxZoom, 1,
          ],
        },
      });

      // Adiciona handler de clique
      if (onClick) {
        map.on('click', circleLayerId, handleClick);
        map.on('mouseenter', circleLayerId, () => {
          map.getCanvas().style.cursor = 'pointer';
        });
        map.on('mouseleave', circleLayerId, () => {
          map.getCanvas().style.cursor = '';
        });
      }
    }

    return () => {
      // Remove event listeners
      if (onClick) {
        map.off('click', circleLayerId, handleClick);
      }

      // Remove layers
      if (map.getLayer(heatLayerId)) {
        map.removeLayer(heatLayerId);
      }
      if (map.getLayer(circleLayerId)) {
        map.removeLayer(circleLayerId);
      }
      // Remove source
      if (map.getSource(sourceId)) {
        map.removeSource(sourceId);
      }

      // Restaura cursor
      map.getCanvas().style.cursor = '';
    };
  }, [sourceId, heatLayerId, circleLayerId, maxZoom, showPoints, onClick, handleClick]);

  // Atualiza dados quando posições mudam
  useEffect(() => {
    const source = map.getSource(sourceId);
    if (source) {
      source.setData(geojsonData);
    }
  }, [sourceId, geojsonData]);

  // Atualiza propriedades de paint dinamicamente
  useEffect(() => {
    if (map.getLayer(heatLayerId)) {
      map.setPaintProperty(heatLayerId, 'heatmap-intensity', [
        'interpolate',
        ['linear'],
        ['zoom'],
        0, intensity * 0.3,
        maxZoom / 2, intensity,
        maxZoom, intensity * 2,
      ]);
    }
  }, [heatLayerId, intensity, maxZoom]);

  useEffect(() => {
    if (map.getLayer(heatLayerId)) {
      map.setPaintProperty(heatLayerId, 'heatmap-radius', [
        'interpolate',
        ['exponential', 1.5],
        ['zoom'],
        0, radius * 0.3,
        maxZoom / 2, radius,
        maxZoom, radius * 2.5,
      ]);
    }
  }, [heatLayerId, radius, maxZoom]);

  useEffect(() => {
    if (map.getLayer(heatLayerId)) {
      map.setPaintProperty(heatLayerId, 'heatmap-color', buildColorExpression(colorScheme));
    }
  }, [heatLayerId, colorScheme]);

  useEffect(() => {
    if (map.getLayer(heatLayerId)) {
      map.setPaintProperty(heatLayerId, 'heatmap-opacity', [
        'interpolate',
        ['linear'],
        ['zoom'],
        maxZoom - 2, opacity,
        maxZoom, 0,
      ]);
    }
  }, [heatLayerId, opacity, maxZoom]);

  return null;
};

// Exporta também os esquemas de cores para uso externo
export const HEATMAP_COLOR_SCHEMES = Object.keys(COLOR_SCHEMES);

export default MapHeatLayer;
